import { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, IconButton, Chip, TextField, Checkbox, LinearProgress,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    FormControlLabel,
} from '@mui/material';
import { ArrowBack, Send, Description } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDispatch } from 'react-redux';
import { SNACKBAR_OPEN } from '../../../store/actions';
import { useGetCustomerAllDocumentsNoRefetch } from '../../../hooks/customer/documents.hooks';
import { sendMsgAll } from '../../../api/customer/files.api';
import { dateObjectToString } from '../../../fromKotlin/nkutils';
import JSDB from '../../../fromKotlin/nk';
import * as React from 'react';

interface DocPickerPanelProps {
    clientId: string;
    clientName: string;
    jid: string;
    docType: string;
    onBack: () => void;
    onSent: () => void;
}

const DOC_TYPE_LABELS: Record<number, string> = {
    0: 'חשבונית מס', 1: 'חשבונית מס/קבלה', 2: 'זיכוי',
    4: 'תעודת משלוח', 7: 'קבלה', 10: 'הזמנה', 14: 'כרטסת',
};

const toDisplayDate = (value: unknown): string => {
    if (!value) return '';
    if (value instanceof Date) return value.toLocaleDateString('he-IL');
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return '';
};

const DocPickerPanel = ({ clientId, clientName, jid, docType, onBack, onSent }: DocPickerPanelProps) => {
    const dispatch = useDispatch();
    const isCarteset = docType === 'carteset';
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [dateFrom, setDateFrom] = useState(prevMonth);
    const [dateTo, setDateTo] = useState(now);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState('');
    const [withPrice, setWithPrice] = useState(false);
    const [sendSum, setSendSum] = useState(false);

    const { data: docs, isLoading, refetch } = useGetCustomerAllDocumentsNoRefetch(
        dateFrom, dateTo, true, clientId, [Number(clientId)], true,
        docType === 'carteset' ? undefined : undefined,
        false, false, undefined,
    );

    useEffect(() => {
        if (!isCarteset) {
            refetch();
        }
    }, [dateFrom, dateTo, isCarteset, refetch]);

    const filteredDocs = useMemo(() => {
        if (isCarteset || !docs) return [];
        let list = docs.filter((d: any) => d.type !== 'debt' || d?.nkObject?.state === 0);
        if (docType === 'carteset') {
            list = list.filter((d: any) => ['tax_note', 'tax_note_cancel', 'tax_pay_note', 'pay_note', 'delivery_note'].includes(d.type));
        }
        if (search) {
            const q = search.toLowerCase();
            list = list.filter((d: any) =>
                (d.docNumber?.toString() || '').includes(q) ||
                String(d.title || '').toLowerCase().includes(q) ||
                toDisplayDate(d.date).toLowerCase().includes(q)
            );
        }
        return list;
    }, [docs, search, docType]);

    const toggleSelect = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === filteredDocs.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filteredDocs.map((d: any) => String(d.id))));
        }
    };

    const selectedDocTypes = useMemo(() => {
        if (isCarteset) return new Set<number>([9]);
        const selectedDocs = filteredDocs.filter((d: any) => selected.has(String(d.id)));
        return new Set<number>(selectedDocs.map((d: any) => d.nkObject?.getConnectedDocType?.() ?? 0));
    }, [filteredDocs, selected, isCarteset]);

    const hasNotes = selectedDocTypes.has(4) || selectedDocTypes.has(11);
    const hasTax = selectedDocTypes.has(0);
    const showPriceOption = hasNotes;
    const showSumOption = hasNotes || hasTax;

    const handleSend = async () => {
        setSending(true);
        try {
            const selectedDocs = filteredDocs.filter((d: any) => selected.has(String(d.id)));
            const ids = isCarteset ? [Number(clientId)] : selectedDocs.map((d: any) => d.id);
            const docTypes = isCarteset ? [9] : selectedDocs.map((d: any) => d.nkObject?.getConnectedDocType?.() ?? 4);
            if (!ids.length) return;
            const client = JSDB().getClient(Number(clientId)).first;
            const phone = client?.getPhoneComplex?.(dateObjectToString(new Date()), 0) || jid.split('@')[0];

            let specialArg: string | undefined;
            if (withPrice && sendSum) specialArg = '3';
            else if (withPrice) specialArg = '1';
            else if (sendSum) specialArg = hasNotes ? '2' : '1';

            await sendMsgAll(
                ids,
                msg || (isCarteset ? '#docs' : 'מסמכים'),
                isCarteset ? 'שליחת כרטסת' : 'שליחת מסמכים',
                docTypes,
                undefined,
                [phone],
                undefined,
                specialArg,
            );

            dispatch({
                type: SNACKBAR_OPEN, open: true,
                message: isCarteset ? 'כרטסת נשלחה בהצלחה' : `נשלחו ${ids.length} מסמכים בהצלחה`,
                variant: 'alert', alertSeverity: 'success',
            });
            onSent();
        } catch (e) {
            dispatch({
                type: SNACKBAR_OPEN, open: true,
                message: 'שליחה נכשלה',
                variant: 'alert', alertSeverity: 'error',
            });
        }
        setSending(false);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 1, backgroundColor: '#f0f2f5', borderBottom: '1px solid #e5e7eb',
            }}>
                <IconButton size="small" onClick={onBack}><ArrowBack fontSize="small" /></IconButton>
                <Description color="primary" sx={{ fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                    {docType === 'carteset' ? 'כרטסת' : 'מסמכים'} — {clientName}
                </Typography>
                {selected.size > 0 && (
                    <Chip label={`${selected.size} נבחרו`} size="small" color="primary" sx={{ ml: 'auto' }} />
                )}
            </Box>

            {!isCarteset && (
                <>
                    {/* Date filters + search */}
                    <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.75, backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            type="date" size="small" label="מתאריך"
                            value={dateFrom.toISOString().slice(0, 10)}
                            onChange={(e) => setDateFrom(new Date(e.target.value))}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 130, '& input': { fontSize: 11, py: 0.5 } }}
                        />
                        <TextField
                            type="date" size="small" label="עד תאריך"
                            value={dateTo.toISOString().slice(0, 10)}
                            onChange={(e) => setDateTo(new Date(e.target.value))}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 130, '& input': { fontSize: 11, py: 0.5 } }}
                        />
                        <TextField
                            size="small" placeholder="חיפוש..."
                            value={search} onChange={(e) => setSearch(e.target.value)}
                            sx={{ flex: 1, minWidth: 80, '& input': { fontSize: 12, py: 0.5 } }}
                        />
                    </Box>

                    {isLoading && <LinearProgress />}

                    {/* Documents table */}
                    <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={filteredDocs.length > 0 && selected.size === filteredDocs.length}
                                            indeterminate={selected.size > 0 && selected.size < filteredDocs.length}
                                            onChange={toggleAll}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>מס׳</TableCell>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>סוג</TableCell>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>תאריך</TableCell>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>סכום</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDocs.map((doc: any) => {
                                    const id = String(doc.id);
                                    const docTypeNum = doc.nkObject?.getConnectedDocType?.() ?? 0;
                                    return (
                                        <TableRow
                                            key={id}
                                            hover
                                            selected={selected.has(id)}
                                            onClick={() => toggleSelect(id)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox size="small" checked={selected.has(id)} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 11 }}>{doc.docNumber || doc.id}</TableCell>
                                            <TableCell sx={{ fontSize: 11 }}>{DOC_TYPE_LABELS[docTypeNum] || doc.type}</TableCell>
                                            <TableCell sx={{ fontSize: 11 }}>{toDisplayDate(doc.date)}</TableCell>
                                            <TableCell sx={{ fontSize: 11, direction: 'ltr' }}>
                                                {doc.total != null ? `₪${Number(doc.total).toFixed(0)}` : ''}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {!isLoading && filteredDocs.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 3, fontSize: 12 }}>
                                            לא נמצאו מסמכים
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            {isCarteset && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 13 }}>
                    כרטסת תישלח כלפי הלקוח עם ההודעה שתקליד למטה
                </Box>
            )}

            {/* Message + send */}
            <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #e5e7eb', backgroundColor: '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <TextField
                    size="small" fullWidth placeholder="הודעה מצורפת (אופציונלי)..."
                    value={msg} onChange={(e) => setMsg(e.target.value)}
                    sx={{ '& input': { fontSize: 12 } }}
                />
                {(showPriceOption || showSumOption) && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {showPriceOption && (
                            <FormControlLabel
                                control={<Checkbox size="small" checked={withPrice} onChange={(e) => setWithPrice(e.target.checked)} />}
                                label={<Typography variant="caption">עם מחירים</Typography>}
                                sx={{ mr: 0 }}
                            />
                        )}
                        {showSumOption && (
                            <FormControlLabel
                                control={<Checkbox size="small" checked={sendSum} onChange={(e) => setSendSum(e.target.checked)} />}
                                label={<Typography variant="caption">{hasNotes ? 'סה״כ בלבד' : 'שלח סיכום'}</Typography>}
                                sx={{ mr: 0 }}
                            />
                        )}
                    </Box>
                )}
                <LoadingButton
                    variant="contained" fullWidth loading={sending}
                    color="success" disabled={(!isCarteset && !selected.size) || sending}
                    onClick={handleSend} startIcon={<Send />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {isCarteset ? 'שלח כרטסת' : `שלח ${selected.size} מסמכים`}
                </LoadingButton>
            </Box>
        </Box>
    );
};

export default DocPickerPanel;
